import { ApiResponse, ApiError, ApiRequestConfig, CacheEntry, HttpMethod, RequestParams, Endpoint } from './types/api';
import { ProductEndpoints } from './endpoints/products';
import { CustomerEndpoints } from './endpoints/customer';
import { Analytics } from './Analytics';

/**
 * Centralized API Manager for all network requests
 * Provides caching, error handling, and extensible architecture
 */
class ApiManagerClass {
  private cache: Map<string, CacheEntry<any>> = new Map();
  private config: ApiRequestConfig;

  // Endpoint groups
  public products: ProductEndpoints;
  public customer: CustomerEndpoints;
  public analytics: Analytics;

  constructor(config: ApiRequestConfig = {}) {
    this.config = {
      timeout: 10000,
      retries: 8,
      cache: true,
      cacheTTL: 5 * 60 * 1000, // 5 minutes default
      ...config,
    };

    // Initialize endpoint groups
    this.products = new ProductEndpoints();
    this.customer = new CustomerEndpoints(process.env.REACT_APP_PLAYMERCE_API_ROOT || 'http://127.0.0.1:8081');
    this.analytics = new Analytics();
  }

  /**
   * Generic request method for future API endpoints
   */
  async request<T>(
    endpoint: Endpoint,
    params?: RequestParams,
    body?: any,
    options: Partial<ApiRequestConfig> = {}
  ): Promise<ApiResponse<T>> {
    const requestConfig = { ...this.config, ...options };
    const cacheKey = this.generateCacheKey(endpoint.url, params);

    // Check cache first
    if (requestConfig.cache && this.isValidCache(cacheKey)) {
      const cachedData = this.cache.get(cacheKey)!;
      return {
        data: cachedData.data,
        status: 200,
        statusText: 'OK (cached)',
      };
    }

    const url = this.buildUrl(endpoint.url, params);
    const method = endpoint.method || 'GET';

    try {
      const response = await this.fetchWithRetry(
        url,
        {
          method,
          headers: {
            'Content-Type': 'application/json',
            ...requestConfig.headers,
          },
          body: body ? JSON.stringify(body) : undefined,
        },
        requestConfig.retries || 0
      );

      if (!response.ok) {
        throw this.createApiError(`HTTP error! status: ${response.status}`, response.status, response.statusText);
      }

      // Handle 204 No Content responses (common for analytics endpoints)
      let data: T;
      if (response.status === 204) {
        data = undefined as T;
      } else {
        data = await response.json();
      }

      // Cache the response (skip caching for 204 responses)
      if (requestConfig.cache && response.status !== 204) {
        this.setCache(cacheKey, data, requestConfig.cacheTTL!);
      }

      return {
        data,
        status: response.status,
        statusText: response.statusText,
      };
    } catch (error) {
      if (error instanceof Error) {
        throw this.createApiError(error.message);
      }
      throw this.createApiError('Unknown error occurred');
    }
  }

  /**
   * GET request helper
   */
  async get<T>(
    endpoint: Endpoint,
    params?: RequestParams,
    options?: Partial<ApiRequestConfig>
  ): Promise<ApiResponse<T>> {
    return this.request<T>({ ...endpoint, method: 'GET' }, params, undefined, options);
  }

  /**
   * POST request helper
   */
  async post<T>(endpoint: Endpoint, body?: any, options?: Partial<ApiRequestConfig>): Promise<ApiResponse<T>> {
    return this.request<T>({ ...endpoint, method: 'POST' }, undefined, body, options);
  }

  /**
   * PUT request helper
   */
  async put<T>(endpoint: Endpoint, body?: any, options?: Partial<ApiRequestConfig>): Promise<ApiResponse<T>> {
    return this.request<T>({ ...endpoint, method: 'PUT' }, undefined, body, options);
  }

  /**
   * DELETE request helper
   */
  async delete<T>(
    endpoint: Endpoint,
    params?: RequestParams,
    options?: Partial<ApiRequestConfig>
  ): Promise<ApiResponse<T>> {
    return this.request<T>({ ...endpoint, method: 'DELETE' }, params, undefined, options);
  }

  /**
   * Clear all cache entries
   */
  clearCache(): void {
    this.cache.clear();
  }

  /**
   * Clear specific cache entry
   */
  clearCacheEntry(key: string): void {
    this.cache.delete(key);
  }

  /**
   * Get cache statistics
   */
  getCacheStats(): { size: number; entries: string[] } {
    return {
      size: this.cache.size,
      entries: Array.from(this.cache.keys()),
    };
  }

  /**
   * Update configuration
   */
  updateConfig(newConfig: Partial<ApiRequestConfig>): void {
    this.config = { ...this.config, ...newConfig };
  }

  // Private methods

  private async fetchWithRetry(url: string, options: RequestInit, retries: number): Promise<Response> {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), this.config.timeout);

      const response = await fetch(url, {
        ...options,
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      // Check for 500 errors and retry if we have attempts left
      if (response.status >= 500 && response.status < 600 && retries > 0) {
        console.warn(`Server error (${response.status}), retrying... (${retries} attempts left)`);
        await this.delay(500 * (4 - retries)); // Exponential backoff: 1s, 2s, 3s
        return this.fetchWithRetry(url, options, retries - 1);
      }

      return response;
    } catch (error) {
      if (retries > 0 && error instanceof Error && error.name !== 'AbortError') {
        console.warn(`Request failed, retrying... (${retries} attempts left)`);
        await this.delay(500 * (4 - retries)); // Exponential backoff: 1s, 2s, 3s
        return this.fetchWithRetry(url, options, retries - 1);
      }
      throw error;
    }
  }

  private buildUrl(baseUrl: string, params?: RequestParams): string {
    if (!params || Object.keys(params).length === 0) {
      return baseUrl;
    }

    const url = new URL(baseUrl);
    Object.entries(params).forEach(([key, value]) => {
      url.searchParams.append(key, String(value));
    });

    return url.toString();
  }

  private generateCacheKey(url: string, params?: RequestParams): string {
    const paramString = params ? JSON.stringify(params) : '';
    return `${url}:${paramString}`;
  }

  private isValidCache(key: string): boolean {
    const entry = this.cache.get(key);
    if (!entry) return false;

    const now = Date.now();
    const isExpired = now - entry.timestamp > entry.ttl;

    if (isExpired) {
      this.cache.delete(key);
      return false;
    }

    return true;
  }

  private setCache<T>(key: string, data: T, ttl: number): void {
    this.cache.set(key, {
      data,
      timestamp: Date.now(),
      ttl,
    });
  }

  private createApiError(message: string, status?: number, statusText?: string): ApiError {
    return {
      message,
      status,
      statusText,
      code: status ? `HTTP_${status}` : 'UNKNOWN_ERROR',
    };
  }

  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }
}

// Export the singleton instance
export const ApiManager = new ApiManagerClass();

// Export endpoint classes for direct use if needed
export { ProductEndpoints } from './endpoints/products';
export { CustomerEndpoints } from './endpoints/customer';
export { Analytics } from './Analytics';

// Export types
export * from './types/api';
export * from './types/analytics';
